import { graphql } from '@/__generated__';
import { GenericMaterializedFilter } from '@/__generated__/graphql';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';

export const GetSemanticModelsByUniqueId = graphql(/* GraphQL */ `
  query SemanticModels(
    $first: Int!
    $after: String
    $environmentId: BigInt!
    $userId: BigInt
    $filter: GenericMaterializedFilter
  ) {
    environment(id: $environmentId, userId: $userId) {
      definition {
        lastUpdatedAt
        semanticModels(first: $first, after: $after, filter: $filter) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              uniqueId
              tags
              description
              projectId
              dimensions {
                name
              }
              entities {
                name
                type
              }
              measures {
                name
              }
            }
          }
        }
      }
    }
  }
`);

type SemanticModelsByEnvironmentParams = {
  first: number;
  after?: string;
  environmentId: number | undefined;
  userId: number | 'loading' | null;
  filter?: Partial<GenericMaterializedFilter>;
  pause?: boolean;
};

/**
 * SemanticModels are Definition Only
 */
export const useDiscoveryDefinitionSemanticModels = ({
  first,
  after,
  environmentId,
  userId,
  filter,
  pause,
}: SemanticModelsByEnvironmentParams) => {
  const query = usePaginatedQuery(
    {
      query: GetSemanticModelsByUniqueId,
      variables: {
        first,
        after,
        environmentId,
        userId: userId as number | null,
        filter,
      },
      pause: pause || !environmentId || !first,
    },
    (data) => {
      const semanticModels = data.environment.definition?.semanticModels;
      const pageInfo = semanticModels?.pageInfo;
      if (semanticModels == null || pageInfo == null) return undefined;
      return {
        totalCount: semanticModels.totalCount ?? null,
        endCursor: pageInfo.endCursor ?? null,
        hasNextPage: pageInfo.hasNextPage,
      };
    },
    (data) => {
      return data.environment.definition?.semanticModels.edges.map((edge) => edge.node);
    },
    (data) => data.environment.definition?.lastUpdatedAt,
  );

  useCacheMonitor({ definition: query.lastUpdatedAt });

  return query;
};
